import * as React from 'react';
import { PopupButton, PopupContent, usePopup } from '@dx-ui/osc-popup';
import { sendInteractionReward } from '../header.utilities';
import { Dialog } from '@dx-ui/osc-dialog-v2';
import { useTranslation } from 'next-i18next';
import { LoginForm } from '@dx-ui/osc-login';
import { Link, Surface } from '@dx-ui/global-ui-prism';
import type { Surfaces } from '@dx-ui/global-ui-prism';
import cx from 'classnames';
import Icon, { type IconName } from '@dx-ui/osc-icon';
import unset from 'lodash/unset';
import type { LoginFormResponse } from '@dx-ui/osc-login';
import { sendReward } from '@dx-ui/framework-conductrics';
import { JoinModal } from '@dx-ui/osc-join-modal-button';
import { useHeaderContext } from '../context/header-context';
import type { NavItemProps, NavItemSection } from '../types';

export function NavItem({ children, isOpen = false, className, ...listItemProps }: NavItemProps) {
  const spacedChild = React.cloneElement(children, {
    className: cx(children.props.className, 'flex-1 self-stretch'),
  });

  return (
    <li
      {...listItemProps}
      className={cx(
        'osc-nav-list-item text--sm group flex h-full items-center border-t-4 border-t-transparent px-1',
        className,
        {
          'osc-nav-list-item-active': isOpen,
        }
      )}
    >
      {spacedChild}
    </li>
  );
}

export function NavItemVisual({
  label,
  icon,
  indicator,
  hasRotatedIndicator,
  section,
}: {
  label: string;
  icon?: IconName;
  indicator?: IconName;
  section: NavItemSection;
  hasRotatedIndicator?: boolean;
}) {
  return (
    <>
      <span className={cx('block min-h-5', section === 'user' ? 'osc-nav-item-text' : null)}>
        {label}
      </span>
      {icon ? (
        <span className="ps-2">
          <Icon
            name={icon}
            className="group-focus-within:hidden group-hover:hidden"
            wrapper="span"
          />
          <Icon
            name={icon}
            variant="solid"
            className={cx(
              'osc-nav-icon-active',
              'hidden group-focus-within:block group-hover:block'
            )}
            wrapper="span"
          />
        </span>
      ) : null}

      {indicator ? (
        <span>
          <Icon
            name={indicator}
            className={cx(
              hasRotatedIndicator ? 'rotate-180' : null,
              'group-focus-within:hidden group-hover:hidden'
            )}
            wrapper="span"
          />
          <Icon
            name={indicator}
            className={cx(
              hasRotatedIndicator ? 'rotate-180' : null,
              'hidden group-focus-within:block group-hover:block'
            )}
            wrapper="span"
          />
        </span>
      ) : null}
    </>
  );
}

type NavItemWithLinkProps = Omit<
  React.ComponentProps<typeof Link>,
  'className' | 'children' | 'icon'
> & {
  section: NavItemSection;
  label: string;
  icon?: React.ReactElement;
  namedIcon?: IconName;
  isWide?: boolean;
  experimentationConfiguration?: CmsExperimentationConfiguration;
  navItemClassName?: string;
};

export const NavItemWithLink: React.FC<NavItemWithLinkProps> = ({
  namedIcon,
  section,
  label,
  isWide,
  experimentationConfiguration,
  navItemClassName,
  ...rest
}) => {
  return (
    <NavItem data-testid="with-link" className={navItemClassName}>
      <Link
        colorTheme="inherit"
        underline={false}
        {...rest}
        data-conductrics-goal={experimentationConfiguration?.goal}
        data-conductrics-value={experimentationConfiguration?.value}
        anchorClassName="h-full flex !transition-none"
        className={cx('flex items-center whitespace-nowrap', isWide ? 'p-2' : 'p-1', {
          'osc-nav-main-link': section === 'navigation',
        })}
      >
        <NavItemVisual section={section} label={label} icon={namedIcon} />
      </Link>
    </NavItem>
  );
};

export type TNavItemWithModal = {
  onLoginAttempt: React.ComponentProps<typeof LoginForm>['onLoginAttempt'];
  dialogTrigger?: React.ReactNode;
  buttonLabel?: string;
  buttonIcon?: IconName;
  onClick?: () => void;
  section: NavItemSection;
  navItemClassName?: string;
  shouldOpenJoinInPage?: boolean;
  locale?: string;
  loggedInButtonRef?: React.RefObject<HTMLButtonElement | null>;
};

export const NavItemWithModal: React.FC<React.PropsWithChildren<TNavItemWithModal>> = ({
  section,
  buttonLabel,
  buttonIcon,
  onClick,
  onLoginAttempt,
  children,
  navItemClassName,
  shouldOpenJoinInPage,
  locale = 'en',
  dialogTrigger,
  loggedInButtonRef,
}) => {
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [showJoinModal, setShowJoinModal] = React.useState(false);
  const {
    user: { analytics },
  } = useHeaderContext();
  const open = (open: boolean) => {
    if (open) {
      sendInteractionReward();
      onClick?.();
      setShowDialog(true);
    }
  };
  const close = () => {
    unset(window.digitalData, 'click');
    setShowDialog(false);
  };

  const handleLogin = (response: LoginFormResponse) => {
    onLoginAttempt?.(response);
    if (!response.error) {
      sendReward('g-IqzR2S0Ai5');
      close();
    }
  };

  const onJoinClick = () => {
    setShowJoinModal(true);
    close();
  };

  const arrayChildren = React.Children.toArray(children);

  const [t] = useTranslation('osc-login');

  const signInButtonRef = React.useRef<React.ComponentRef<'button'>>(null);

  return (
    <>
      {!shouldOpenJoinInPage ? (
        <JoinModal
          analytics={analytics}
          isOpen={showJoinModal}
          language={locale}
          onLoginAttempt={onLoginAttempt}
          onDismiss={() => {
            setShowJoinModal(false);
            setTimeout(() => signInButtonRef.current?.focus(), 100);
          }}
          onSuccessfulLogin={() => {
            setShowJoinModal(false);
            setTimeout(() => loggedInButtonRef?.current?.focus(), 1000);
          }}
        />
      ) : null}
      {!dialogTrigger ? (
        <NavItem data-testid="with-modal" className={navItemClassName}>
          <button
            type="button"
            className="text--sm group flex h-full items-center whitespace-nowrap px-1"
            data-testid="with-modal"
            ref={signInButtonRef}
            onClick={() => open(true)}
            data-dd-action-name="Sign In Modal"
          >
            <NavItemVisual label={buttonLabel ?? t('signIn')} icon={buttonIcon} section={section} />
          </button>
        </NavItem>
      ) : null}
      <Dialog
        isOpen={showDialog && !showJoinModal}
        onDismiss={close}
        size="md"
        title={t('signIn')}
        onOpenChange={open}
        dialogTrigger={dialogTrigger}
      >
        {React.Children.map(arrayChildren, (child) => {
          if (React.isValidElement(child) && child.type === LoginForm) {
            const loginFormElement = child as React.ReactElement<
              React.ComponentProps<typeof LoginForm>
            >;
            const loginFormConfig = !shouldOpenJoinInPage
              ? {
                  ...(loginFormElement.props.loginFormConfig ?? {
                    showCreatePasswordLink: false,
                    showForgetInfoLink: true,
                    showJoinLink: true,
                    showSecureInfoLink: false,
                  }),
                  onJoinClick,
                  joinFromModal: true,
                }
              : loginFormElement.props.loginFormConfig;

            return React.cloneElement(loginFormElement, {
              onLoginAttempt: handleLogin,
              isInModal: true,
              loginFormConfig,
              analytics,
              isLegacySignInAnalyticsEnabled: analytics?.isLegacySignInAnalyticsEnabled,
            });
          }
          return child;
        })}
      </Dialog>
    </>
  );
};

type TNavItemWithPopup = {
  section: NavItemSection;
  onClick?: () => void;
  popupButtonRef?: React.RefObject<HTMLButtonElement | null>;
  label: string;
  icon?: IconName;
  indicator?: IconName;
  rotateOnOpen?: boolean;
  experimentationConfiguration?: CmsExperimentationConfiguration;
  surfaceType?: Surfaces;
  isTranslucent?: boolean;
};

export const NavItemWithPopup: React.FC<React.PropsWithChildren<TNavItemWithPopup>> = ({
  children,
  label,
  icon,
  rotateOnOpen,
  onClick,
  popupButtonRef,
  section,
  indicator,
  experimentationConfiguration,
  surfaceType,
  isTranslucent,
}) => {
  const { isOpen } = usePopup();

  return (
    <>
      <NavItem isOpen={isOpen} data-testid="with-popup">
        <PopupButton
          ref={popupButtonRef}
          data-conductrics-goal={experimentationConfiguration?.goal}
          data-conductrics-value={experimentationConfiguration?.value}
          onClick={() => {
            sendInteractionReward();
            onClick?.();
          }}
          className={cx('flex items-center whitespace-nowrap p-2', {
            'osc-nav-main-link': section === 'navigation',
          })}
        >
          <NavItemVisual
            label={label}
            section={section}
            indicator={indicator}
            icon={icon}
            hasRotatedIndicator={!isOpen && rotateOnOpen}
          />
        </PopupButton>
      </NavItem>
      <li>
        <PopupContent
          className={cx('!osc-popup-margin !w-screen !p-0 motion-safe:animate-fadein', {
            '!bg-transparent': isTranslucent,
          })}
          popupStyles={{ left: 0 }}
        >
          <Surface
            type={surfaceType || 'base'}
            className={cx('osc-nav-drawer', { '!bg-translucent bg-blur': isTranslucent })}
          >
            {children}
          </Surface>
        </PopupContent>
      </li>
    </>
  );
};
